import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useRFQ } from '../contexts/RFQContext';
import { trpClient } from '../client/trpClient';
import type { RFQEndUser } from '../types/rfq.types';
import { wizardStepPath } from '../utils/wizardRoutes';
import {
  ACCEPTED_TYPES, MAX_UPLOAD_FILES, MAX_UPLOAD_TOTAL_BYTES, MAX_UPLOAD_TOTAL_MB, overTotalMessage, totalBytes,
} from '../utils/uploadLimits';

/**
 * First-run dashboard: turns a dropped BOM into a real RFQ. There is no RFQ
 * to upload against yet, so the order is create → upload → land on step 1,
 * where the wizard re-hydrates the documents it already owns. Both halves are
 * the paths the wizard uses (`createRFQ` from RFQContext, the same
 * `uploadDocuments` endpoint, the same accepted-type list) — nothing here is
 * a second implementation of upload.
 *
 * Dropping a BOM does NOT create anything on its own. The files are held and
 * the naming popup opens seeded with a name read off the first file, so the
 * buyer confirms or edits it — nothing exists until they do. Cancelling
 * discards the batch and leaves no record behind.
 */

/**
 * How Robbie says the accepted-type list out loud. Kept beside the machine
 * list it summarises: change `ACCEPTED_TYPES` and this sentence moves too.
 */
const ACCEPTED_IN_WORDS = 'Excel, CSV, PDF, Word, plain text, images and ZIP folders';

/** "amr-drive_bom v2.xlsx" → "Amr drive bom v2" — a name, not a filename. */
const projectNameFrom = (file: File): string => {
  const stem = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!stem) return 'New project';
  return (stem.charAt(0).toUpperCase() + stem.slice(1)).slice(0, 80);
};

export const useFirstRfqUpload = () => {
  const navigate = useNavigate();
  const { createRFQ } = useRFQ();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Files wait here between the drop and the confirmed name.
  const [pending, setPending] = useState<File[] | null>(null);

  const accept = useCallback(
    (files: File[]) => {
      if (files.length === 0 || starting) return;
      const batch = files.slice(0, MAX_UPLOAD_FILES);
      if (totalBytes(batch.map((f) => f.size)) > MAX_UPLOAD_TOTAL_BYTES) {
        setError(overTotalMessage());
        return;
      }
      setError(null);
      setPending(batch);
    },
    [starting]
  );

  /** Cancelling the popup throws the batch away — no RFQ, no upload, no trace. */
  const discard = useCallback(() => {
    if (starting) return;
    setPending(null);
  }, [starting]);

  /** The confirmed name is the first moment anything is created. */
  const confirmName = useCallback(
    async (projectName: string, endUser?: RFQEndUser) => {
      if (!pending || pending.length === 0) return;
      setStarting(true);
      setError(null);
      try {
        const rfq = await createRFQ(projectName, endUser);
        await trpClient.uploadDocuments(rfq.rfqId, pending);
        setPending(null);
        navigate(wizardStepPath(rfq.rfqId, { step: 1, subStep: 1 }));
      } catch (err) {
        setStarting(false);
        // Rethrow so the popup shows the failure and stays open with the
        // typed name intact, rather than closing on a silent error.
        throw err;
      }
    },
    [createRFQ, navigate, pending]
  );

  // Rejections are named, not generalised: the buyer should know which file
  // I refused and why, without opening a spec sheet.
  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const first = rejections[0];
    const tooBig = first?.errors.some((e) => e.code === 'file-too-large');
    const tooMany = first?.errors.some((e) => e.code === 'too-many-files');
    if (tooMany) {
      setError(`I can take ${MAX_UPLOAD_FILES} files at a time — drop the rest once we're inside.`);
    } else if (tooBig) {
      setError(`${first.file.name} is over ${MAX_UPLOAD_TOTAL_MB} MB, which is more than I can take in one go.`);
    } else {
      setError(`I can't read ${first?.file.name ?? 'that file'} — I work with ${ACCEPTED_IN_WORDS}.`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: accept,
    onDropRejected,
    accept: ACCEPTED_TYPES,
    multiple: true,
    maxFiles: MAX_UPLOAD_FILES,
    maxSize: MAX_UPLOAD_TOTAL_BYTES,
    // The zone is a drop target only; "Browse files" is the click and
    // keyboard path, so the picker can never open twice from one gesture.
    noClick: true,
    noKeyboard: true,
    disabled: starting,
  });

  return {
    dropzone: { rootProps: getRootProps(), inputProps: getInputProps(), isDragActive },
    openPicker: open,
    /** True while the popup should be showing — i.e. files are waiting on a name. */
    naming: pending !== null,
    /** Seed for the popup's field, read off the first dropped file. */
    suggestedName: pending && pending.length > 0 ? projectNameFrom(pending[0]) : '',
    confirmName,
    discard,
    starting,
    error,
  };
};

export default useFirstRfqUpload;
